import { BIOMES } from '../data/biomes.js';
import { BOSS_MOB_IDS, BOSSES } from '../data/bosses.js';
import { BIOME_ORDER, CONTRACT_COUNTS, DAILY_CONTRACT_GOLD, DAILY_CONTRACT_SLOTS, dailyContractItems, KILL_BIOME_COUNT, killMobCount, WEEKLY_CONTRACT_FACTOR, weeklyContractItems, } from '../data/contracts.js';
import { biomeOfLevel } from '../data/loot.js';
import { seededRng } from './random.js';
/**
 * Daily and weekly contracts (ADR 0051), pure. A game day runs from 6 in
 * the morning, Paris time, to the next; a week from Monday 6. The contracts
 * of a period are drawn from a seed of the character and the period, then
 * written down once (the level may move on, the contracts do not); what
 * they ask is read in the career against the counters when they were drawn.
 */
const DAY_MS = 24 * 60 * 60 * 1000;
const RESET_HOUR = 6;
const PARIS = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Paris',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
});
function parisTime(now) {
    const parts = Object.fromEntries(PARIS.formatToParts(new Date(now)).map((p) => [p.type, p.value]));
    const hours = Number(parts.hour);
    const minutes = Number(parts.minute);
    const seconds = Number(parts.second);
    return {
        date: `${parts.year ?? ''}-${parts.month ?? ''}-${parts.day ?? ''}`,
        sinceMidnight: ((hours * 60 + minutes) * 60 + seconds) * 1000 + (now % 1000),
    };
}
/** A calendar date moved by whole days, on the date alone (no time zone involved). */
function addDays(date, days) {
    const moved = new Date(Date.parse(`${date}T00:00:00Z`) + days * DAY_MS);
    return moved.toISOString().slice(0, 10);
}
/** The game day `now` falls in, named by its calendar date: before 6 am it is still yesterday. */
export function gameDay(now) {
    const paris = parisTime(now);
    return paris.sinceMidnight < RESET_HOUR * 3_600_000 ? addDays(paris.date, -1) : paris.date;
}
/** The game week, named `W` and the date of its Monday. */
export function gameWeek(now) {
    const day = gameDay(now);
    const weekday = (new Date(`${day}T00:00:00Z`).getUTCDay() + 6) % 7;
    return `W${addDays(day, -weekday)}`;
}
/** The seven game days of a week, Monday first. */
export function daysOfWeek(week) {
    const monday = week.slice(1);
    return Array.from({ length: 7 }, (_, index) => addDays(monday, index));
}
/**
 * Milliseconds until the next game day, for the countdown. Off by an hour on
 * the two nights the clocks change; the day's name, which is what counts, is not.
 */
export function msUntilNextDay(now) {
    const since = parisTime(now).sinceMidnight - RESET_HOUR * 3_600_000;
    return since >= 0 ? DAY_MS - since : -since;
}
export function msUntilNextWeek(now) {
    const day = gameDay(now);
    const weekday = (new Date(`${day}T00:00:00Z`).getUTCDay() + 6) % 7;
    return msUntilNextDay(now) + (6 - weekday) * DAY_MS;
}
/** FNV-1a: a stable seed from a character and a period. */
export function seedOf(text) {
    let hash = 0x811c9dc5;
    for (let index = 0; index < text.length; index += 1) {
        hash ^= text.charCodeAt(index);
        hash = Math.imul(hash, 0x01000193);
    }
    return hash >>> 0;
}
function pick(items, rng) {
    const item = items[Math.floor(rng() * items.length)];
    if (item === undefined)
        throw new Error('pick from an empty list');
    return item;
}
/** The biome a contract aims at: the player's own band, now and then the one before. */
function aimedBiome(level, rng) {
    const own = biomeOfLevel(level);
    const index = BIOME_ORDER.indexOf(own);
    const before = BIOME_ORDER[index - 1];
    return before !== undefined && rng() < 0.35 ? before : own;
}
/** The creatures of a biome's dens: what `kill-biome` counts and `kill-mob` names. */
export function contractMobsOf(biome) {
    return [...new Set(BIOMES[biome].dens.map((den) => den.mob))];
}
function dailyReward(biome) {
    return { gold: DAILY_CONTRACT_GOLD[biome], items: dailyContractItems(biome) };
}
function dailyContract(id, kind, biome, rng) {
    const reward = dailyReward(biome);
    switch (kind) {
        case 'kill-mob':
            return { id, kind, biome, mob: pick(contractMobsOf(biome), rng), count: killMobCount(biome), reward };
        case 'kill-biome':
            return { id, kind, biome, count: KILL_BIOME_COUNT[biome], reward };
        case 'camp':
        case 'upgrade':
        case 'loot':
        case 'cast':
            return { id, kind, biome, count: CONTRACT_COUNTS[kind], reward };
        default:
            throw new Error(`not a daily contract: ${kind}`);
    }
}
/** Kinds after the first, which always hunts: two of these, never the same twice. */
const DAILY_EXTRAS = ['camp', 'upgrade', 'loot', 'cast'];
/** The three contracts of a game day for a character of `level`. */
export function drawDailyContracts(characterId, day, level) {
    const rng = seededRng(seedOf(`${characterId}:${day}`));
    const kinds = [rng() < 0.5 ? 'kill-mob' : 'kill-biome'];
    const extras = [...DAILY_EXTRAS];
    while (kinds.length < DAILY_CONTRACT_SLOTS.length) {
        const index = Math.floor(rng() * extras.length);
        kinds.push(...extras.splice(index, 1));
    }
    return DAILY_CONTRACT_SLOTS.map((slot, index) => dailyContract(slot, kinds[index] ?? 'kill-biome', aimedBiome(level, rng), rng));
}
/** The zone bosses of the biomes up to the player's own: those within reach. */
export function bossesWithinReach(level) {
    const reach = BIOME_ORDER.indexOf(biomeOfLevel(level));
    return BOSS_MOB_IDS.filter((id) => BIOME_ORDER.indexOf(BOSSES[id].biome) <= reach);
}
/** The week's contract: bosses, dailies or world events, paid five dailies of the player's biome. */
export function drawWeeklyContract(characterId, week, level) {
    const rng = seededRng(seedOf(`${characterId}:${week}`));
    const biome = biomeOfLevel(level);
    const reward = {
        gold: DAILY_CONTRACT_GOLD[biome] * WEEKLY_CONTRACT_FACTOR,
        items: weeklyContractItems(biome),
    };
    const kind = pick(['bosses', 'dailies', 'events'], rng);
    if (kind === 'bosses') {
        const bosses = bossesWithinReach(level);
        const count = Math.min(CONTRACT_COUNTS.bosses, bosses.length);
        return { id: 'w', kind, biome, bosses, count, reward };
    }
    return { id: 'w', kind, biome, count: CONTRACT_COUNTS[kind], reward };
}
/** The counters a contract compares against, as they stand: its baseline when drawn. */
export function contractCounters(contract, facts) {
    const { career } = facts;
    switch (contract.kind) {
        case 'kill-mob':
            return { [contract.mob ?? '']: career.kills[contract.mob ?? ''] ?? 0 };
        case 'kill-biome':
            return {
                kills: contractMobsOf(contract.biome).reduce((sum, id) => sum + (career.kills[id] ?? 0), 0),
            };
        case 'camp':
            return { camps: career.camps[contract.biome] ?? 0 };
        case 'upgrade':
            return { upgrades: career.upgrades };
        case 'loot':
            return { loot: career.itemsLooted };
        case 'cast':
            return { casts: career.spellsCast };
        case 'events':
            return { events: career.events };
        case 'dailies':
            return { dailies: facts.dailiesClaimed };
        case 'bosses':
            return Object.fromEntries((contract.bosses ?? []).map((id) => [id, career.kills[id] ?? 0]));
    }
}
/** How far a contract is, capped at its count. A boss counts once however often it falls. */
export function contractProgress(contract, baseline, facts) {
    const now = contractCounters(contract, facts);
    let value = 0;
    if (contract.kind === 'bosses') {
        for (const [id, count] of Object.entries(now))
            if (count > (baseline[id] ?? 0))
                value += 1;
    }
    else {
        for (const [key, count] of Object.entries(now))
            value += Math.max(0, count - (baseline[key] ?? 0));
    }
    return Math.min(contract.count, value);
}
//# sourceMappingURL=contracts.js.map